import { ChangeDetectionStrategy, Component, Inject, Injector, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { GitQuery } from '@y42/git';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { combineLatest, delay, map, of, switchMap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Asset, isFeatureUIModel } from '../../models';
import { Y42FeatureTypeToRoute, Y42RouteDetailTabs, Y42RouterService, Y42RouteToFeatureType } from '../../routing';
import { SIDEBAR_ROUTER_OUTLET_NAME } from '../../shell/sidebar';
import { AssetSidebarTab } from './asset-sidebar-tab.model';
import { ASSET_SIDEBAR_TABS } from './asset-sidebar-tabs.const';
import { IS_ASSET_SIDEBAR_ROUTED } from './asset-sidebar.model';
import { AssetSidebarService } from './asset-sidebar.service';

@UntilDestroy()
@Component({
  selector: 'y42-asset-sidebar',
  templateUrl: './asset-sidebar.component.html',
  styleUrls: ['./asset-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetSidebarComponent {
  constructor(
    private readonly assetSidebarService: AssetSidebarService,
    private readonly routerService: Y42RouterService,
    private readonly router: Router,
    private readonly gitQuery: GitQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly orgQuery: OrgQuery,
    // private readonly featureFlagService: FeatureFlagService,
    private readonly injector: Injector,
    @Optional() @Inject(IS_ASSET_SIDEBAR_ROUTED) readonly isAssetSidebarRouted?: boolean,
  ) {}

  private readonly activeTabType$ = this.assetSidebarService.activeTabType$;

  readonly state = connectState(this, {
    isLoading: this.assetSidebarService.loading$.pipe(
      // make sure the progress-bar is visible for at least 500ms
      switchMap((loading) => (loading ? of(loading) : of(loading).pipe(delay(500)))),
    ),
    activeTabType: this.activeTabType$,
    activeTab: this.activeTabType$.pipe(map((activeTabType) => ASSET_SIDEBAR_TABS[activeTabType])),
    tabs: this.assetSidebarService.asset$.pipe(
      map((asset) =>
        Object.values(ASSET_SIDEBAR_TABS).filter((tab) => {
          if (!tab.hidden) {
            return true;
          }

          return !tab.hidden(asset, this.injector);
        }),
      ),
    ),
    asset: this.assetSidebarService.asset$,
    isRedirectToDetailViewVisible: combineLatest([
      this.router.events.pipe(
        map(() => this.routerService.url),
        distinctUntilChanged(),
      ),
      this.assetSidebarService.asset$,
    ]).pipe(
      map(([url, asset]) => {
        const activeFeatureType = Y42RouteToFeatureType[this.routerService.getFeatureRouteFromURL(url)];

        if (asset?.featureType === activeFeatureType) {
          return !this.isOverviewTab(url);
        }

        return true;
      }),
    ),
  });

  readonly trackByType = getTrackByFn<AssetSidebarTab>('type');

  setActiveTab(tab: AssetSidebarTab) {
    this.assetSidebarService.setActiveTab(tab.type);
    this.assetSidebarService.setReadOnly(true);
  }

  closeSidebar() {
    this.assetSidebarService.close();
  }

  async redirectToDetailView() {
    const asset = this.state.asset as Asset | undefined;
    if (!asset?.featureType) {
      return;
    }

    const route = Y42FeatureTypeToRoute[asset.featureType];
    let id;

    if (isFeatureUIModel(asset)) {
      id = asset.$name;
    } else {
      id = asset.$parentFolders[0];
    }

    if (!id) {
      return;
    }

    await this.routerService.navigateToDetail(route, id);
  }

  private isOverviewTab(url: string): boolean {
    const cleanUrl = url
      .substring(0, url.indexOf(`/${SIDEBAR_ROUTER_OUTLET_NAME}:`))
      .replace(this.orgQuery.getActive()?.slug ?? '', '')
      .replace(this.orgSpaceQuery.getActiveId() ?? '', '')
      .replace(this.gitQuery.getActiveBranch()?.name ?? '', '');

    const overviewPath = `/${Y42RouteDetailTabs.Overview}`;
    const result = cleanUrl.includes(overviewPath);
    return result;
  }
}
